/**
 * DeliveryModePills — the canonical filter-chip row for picking a
 * delivery mode, so labels, icons, ordering and styling stay the same
 * wherever a delivery mode is chosen.
 *
 * Pages decide *which* modes to show (estimating shows mailing only for
 * mailing products) via the optional `modes` prop; ordering follows
 * `DELIVERY_MODE_ORDER`.
 */

import type { DeliveryMode } from "@shared-core/data/delivery-types";
import {
  DELIVERY_MODE_LABELS,
  DELIVERY_MODE_ORDER,
} from "@shared-core/data/delivery-types";
import {
  Calculator,
  Package,
  PauseCircle,
  Send,
  Truck,
  Users,
} from "lucide-react";

const MODE_ICONS: Record<
  DeliveryMode,
  React.ComponentType<{ className?: string }>
> = {
  tbc: Calculator,
  ship: Truck,
  collect: Users,
  multi: Package,
  hold: PauseCircle,
  mailing: Send,
};

export function DeliveryModePills({
  current,
  onChange,
  modes = DELIVERY_MODE_ORDER,
  disabled = false,
  ariaLabel = "Delivery mode",
}: {
  current: DeliveryMode;
  onChange: (mode: DeliveryMode) => void;
  /** Subset of modes to render. Defaults to all six. The order in
   *  `DELIVERY_MODE_ORDER` is preserved regardless of subset shape. */
  modes?: readonly DeliveryMode[];
  disabled?: boolean;
  ariaLabel?: string;
}) {
  // Use canonical order, filtered by what's allowed.
  const visible = DELIVERY_MODE_ORDER.filter((m) => modes.includes(m));
  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label={ariaLabel}>
      {visible.map((m) => {
        const active = m === current;
        const Icon = MODE_ICONS[m];
        return (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={active}
            aria-pressed={active}
            onClick={() => onChange(m)}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
              active
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "bg-background text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            <Icon className="size-4" />
            {DELIVERY_MODE_LABELS[m]}
          </button>
        );
      })}
    </div>
  );
}

/** Icon component for a given mode — exposed so non-pill surfaces
 *  (estimate-priced-for banner, summary chips) can match the lexicon. */
export function deliveryModeIcon(
  mode: DeliveryMode,
): React.ComponentType<{ className?: string }> {
  return MODE_ICONS[mode];
}
